import { PlusCircleIcon } from "@heroicons/react/24/solid";
import React, { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import MachineSection from "../sections/MachineSection";
import { api } from "../../../../../services/api/api";
import { useWorkorderMachine } from "../../../../../hooks/useWorkorderMachine";
import MachineDraftRow from "../../../../../components/workorders/machines/MachineDraftRow";
import { t } from "i18next";
import { Button } from "@material-tailwind/react";
import { useWorkorderContext } from "../../../../../context/WorkorderContext";


interface MachinesTabProps {
  workorder: EasyTech.Workorder | null;
  canEdit?: boolean;
}

const MachinesTab: React.FC<MachinesTabProps> = ({
  workorder,
  canEdit = true
}) => {
  const { editingPartId } = useWorkorderContext();
  // État pour contrôler l'affichage du draft
  const [showMachineDraft, setShowMachineDraft] = useState(false);
  
  // Supprimer le useDialogs car nous n'utilisons plus le dialogue modal
  const { addMachine } = useWorkorderMachine(workorder?.id || "");
  const [machinesCustomer, setMachinesCustomer] = useState<EasyTech.Machine[]>([]);

  // Gestionnaire pour l'ajout d'une machine
  const handleAddMachine = () => {
    setShowMachineDraft(true);
  };

  // Gestionnaire pour l'annulation de l'ajout
  const handleCancelAddMachine = () => {
    setShowMachineDraft(false);
  };

  // Gestionnaire pour la sauvegarde de la machine
  const handleSaveMachine = useCallback(
    async (machineData: { machine: EasyTech.Machine; notes: string }) => {
      try {
        await addMachine({
          machine: { id: machineData.machine.id },
          notes: machineData.notes,
        });
        toast.success(t("workorder:machines:success:machineAdded"));
        setShowMachineDraft(false); // Fermer le draft après sauvegarde
      } catch (err) {
        console.error("Erreur lors de l'ajout de la machine", err);
        if (err instanceof Error && err.message === "Cette machine est déjà présente dans le workorder") {
          toast.error(t("workorder:machines:error:machineAlreadyInWorkorder"));
        } else {
          toast.error(t("workorder:machines:error:machineAddError"));
        }
      }
    },
    [addMachine]
  );

  // Chargement des machines du client
  useEffect(() => {
    const fetchCustomerMachines = async () => {
      if (!workorder?.customer?.id) return;
      try {
        const machines = await api.get(
          `users/customers/${workorder.customer.id}/machines`
        );
        setMachinesCustomer(machines as EasyTech.Machine[]);
      } catch (err) {
        console.error("workorder:machines:error:machineLoadError", err);
      }
    };

    fetchCustomerMachines();
  }, [workorder?.customer?.id]);

  if (!workorder) return null;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4 border-b border-gray-300 pb-4">
        <h2 className="text-xl font-medium text-gray-900">
          {t("workorder:editIntervention")} <span className="text-gray-500 text-sm">{workorder.ref}</span>
        </h2>
        <Button
          onClick={handleAddMachine}
          variant="filled"
          size="md"
          className="flex items-center gap-2 bg-blue-50 shadow-md text-sm text-blue-500 px-3 rounded-lg font-thin normal-case"
          disabled={showMachineDraft || !canEdit || !!editingPartId} // Désactiver si pas le droit ou édition en cours
        >
          <PlusCircleIcon className="h-5 w-5 text-blue-500" />
          {t("workorder:addMachine")}
        </Button>
      </div>

      {/* Afficher le draft de machine si nécessaire */}
      {showMachineDraft && (
        <MachineDraftRow
          machines={machinesCustomer}
          onSave={handleSaveMachine}
          onCancel={handleCancelAddMachine}
        />
      )}

      {workorder.machines?.length > 0 ? (
        workorder.machines.map((machine) => (
            <MachineSection
              key={machine.id}
              workorderId={workorder.id}
              woMachineId={machine.id}
              notes={machine.notes || ""}
              machinesCustomer={machinesCustomer}
              canEdit={canEdit}
            />
        ))
      ) : (
        !showMachineDraft && (
          <div className="text-center p-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <p className="text-gray-500 mt-1 mb-4">
              {t("workorder:machines:errors:noMachine")}
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default MachinesTab;
